// The passages behind an observation, and how to find them again in the entry.
//
// The Worker only stores a citation after checking the quote against the entry the person wrote
// (collective-vault-worker/src/citations.ts), so anything that arrives here is real. What the
// client still has to solve is drift: entries are editable, so the stored offsets can be stale by
// the time someone taps a claim. We therefore treat `quote` as the truth and the offsets as a
// hint, and — when the passage is genuinely gone — show no highlight rather than mark the wrong
// words in somebody's journal.

/** One verified quote from one entry, backing part of an observation. */
export interface Citation {
  id: string;
  entryId: string;
  /** The person's own words, exactly as they wrote them. */
  quote: string;
  /** Offsets into the entry body when the claim was written. A hint, not the truth. */
  start: number;
  end: number;
  /** The span of Oreia's sentence this quote supports — what becomes tappable. */
  clause: string | null;
}

/**
 * Fold away differences that are typographic rather than lexical, keeping a map from each
 * normalised character back to its index in the original so a hit stays highlightable.
 */
function normaliseWithMap(input: string): { text: string; map: number[] } {
  const out: string[] = [];
  const map: number[] = [];
  let lastWasSpace = true;

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      if (!lastWasSpace) { out.push(' '); map.push(i); lastWasSpace = true; }
      continue;
    }
    lastWasSpace = false;

    let c = ch;
    if (c === '\u2018' || c === '\u2019' || c === '\u02BC') c = "'";
    else if (c === '\u201C' || c === '\u201D') c = '"';
    else if (c === '\u2013' || c === '\u2014' || c === '\u2212') c = '-';
    else if (c === '\u2026') c = '.';
    out.push(c.toLowerCase());
    map.push(i);
  }

  if (out.length > 0 && out[out.length - 1] === ' ') { out.pop(); map.pop(); }
  return { text: out.join(''), map };
}

function normalise(input: string): string {
  return normaliseWithMap(input).text;
}

/** Locate `needle` in `haystack`, tolerating typography. Offsets are into the original string. */
export function findSpan(haystack: string, needle: string): { start: number; end: number } | null {
  const n = normalise(needle);
  if (n.length < 3) return null;

  const { text, map } = normaliseWithMap(haystack);
  const at = text.indexOf(n);
  if (at < 0) return null;

  const start = map[at];
  const last = map[at + n.length - 1];
  if (start === undefined || last === undefined) return null;
  return { start, end: last + 1 };
}

/**
 * Where this citation sits in the entry *now*. Trusts the stored offsets only while the text there
 * still matches, then falls back to searching, then gives up — a null means "open the entry, say
 * the passage has changed, and highlight nothing".
 */
export function resolveCitation(
  body: string,
  citation: Pick<Citation, 'quote' | 'start' | 'end'>,
): { start: number; end: number } | null {
  const { start, end, quote } = citation;
  if (start >= 0 && end <= body.length && start < end) {
    if (normalise(body.slice(start, end)) === normalise(quote)) return { start, end };
  }
  return findSpan(body, quote);
}

interface CitationRow {
  id?: unknown;
  entry_id?: unknown;
  quote?: unknown;
  start?: unknown;
  end?: unknown;
  clause?: unknown;
}

/** Parse the decrypted citations JSON, ignoring anything malformed rather than throwing. */
export function parseCitations(raw: string): Citation[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out: Citation[] = [];
  for (const row of parsed as CitationRow[]) {
    if (!row || typeof row.quote !== 'string' || typeof row.entry_id !== 'string') continue;
    out.push({
      id: typeof row.id === 'string' ? row.id : `c${out.length + 1}`,
      entryId: row.entry_id,
      quote: row.quote,
      start: typeof row.start === 'number' ? row.start : -1,
      end: typeof row.end === 'number' ? row.end : -1,
      clause: typeof row.clause === 'string' && row.clause ? row.clause : null,
    });
  }
  return out;
}

export interface CitedSegment {
  text: string;
  citation: Citation | null;
}

/**
 * Split an observation into plain and cited runs, so the parts Oreia can prove are the parts the
 * reader can tap.
 *
 * Clauses come back from a language model, so one may not appear in the final sentence at all, and
 * two may overlap. Unmatched clauses are simply not underlined — the citation still exists and is
 * still listed as a source; it just doesn't claim a span it can't point at.
 */
export function citedSegments(text: string, citations: Citation[]): CitedSegment[] {
  const spans: Array<{ start: number; end: number; citation: Citation }> = [];

  for (const citation of citations) {
    if (!citation.clause) continue;
    const span = findSpan(text, citation.clause);
    if (span) spans.push({ ...span, citation });
  }

  spans.sort((a, b) => a.start - b.start);

  const segments: CitedSegment[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.start < cursor) continue;   // overlaps something already claimed
    if (span.start > cursor) segments.push({ text: text.slice(cursor, span.start), citation: null });
    segments.push({ text: text.slice(span.start, span.end), citation: span.citation });
    cursor = span.end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), citation: null });

  return segments;
}
